import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const allFaqs = [
  {
    q: 'Who can submit?',
    a: "Anyone, anywhere in the world. All nationalities, all languages — English subtitles required for non-English dialogue. First-time filmmakers, veterans, students, professionals. If you made it, we want to see it.",
  },
  {
    q: 'Does my film need to be a premiere?',
    a: "World, international, and online premiere status is preferred but not required. Films currently on Vimeo, YouTube, or other platforms are fully eligible. We care about the work, not the premiere status.",
  },
  {
    q: 'What is the Scene category?',
    a: "A scene submission is a single scene — any genre, any length. It could be a scene from a larger work in progress, a standalone piece, or a proof of concept. It's the lowest-barrier entry point in the festival and is judged purely on the quality of the work within that scene.",
  },
  {
    q: 'What is a Vertical Film?',
    a: "Vertical films are shot specifically for portrait orientation — the format native to phones and platforms like TikTok and Instagram. We believe vertical is a legitimate cinematic format and it deserves its own category and its own prize.",
  },
  {
    q: 'How does the watch party work?',
    a: "Selected films are streamed on Mindi.TV during the festival window. Ticket holders join a synchronized screening — everyone watches the same film at the same moment, with live reactions and community chat. After the screening, filmmakers join for a live Q&A. It's the closest thing streaming has to a real theater experience.",
  },
  {
    q: 'Do you acquire rights to my film?',
    a: "No. Submitting grants Synema the right to screen your film during the festival window and to use stills and clips for promotional purposes only. You retain full ownership of your work.",
  },
  {
    q: 'Are fee waivers available?',
    a: "Yes. Waivers are available for filmmakers from lower-income countries and first-time filmmakers. Email synemafest@gmail.com before submitting and we'll sort it out.",
  },
  {
    q: 'How are winners selected?',
    a: "Most categories are decided by a jury of film professionals. The Audience Choice Award is determined by real-time watch party engagement data — completion rates, reactions, and live participation. The audience picks that one, not us.",
  },
];

const FAQItem = ({ faq }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-b border-white/[0.06] py-6">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center gap-4 text-left"
      >
        <span className="text-[0.95rem] font-medium text-white">{faq.q}</span>
        <span className={`text-teal text-xl flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}>
          +
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="text-[0.88rem] font-light text-white/[0.55] leading-[1.8] pt-3">
              {faq.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQ = () => {
  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-[720px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="font-mono text-[0.7rem] tracking-[0.25em] uppercase text-teal mb-4">
            FAQ
          </div>
          <h1 className="font-display text-[clamp(2.5rem,5vw,4rem)] tracking-[0.04em] leading-none text-white mb-6">
            Common questions.
          </h1>
          <p className="text-[1rem] font-light leading-[1.8] text-white/[0.65] mb-12">
            Everything you need to know about submitting, attending, and sponsoring Synema.
          </p>
        </motion.div>

        <div>
          {allFaqs.map((faq, i) => (
            <FAQItem key={i} faq={faq} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-[0.88rem] font-light text-white/50">
            Still have questions? Email{' '}
            <a href="mailto:synemafest@gmail.com" className="text-teal font-medium">
              synemafest@gmail.com
            </a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
